import { ActionButton, FontSizes, FontWeights, getTheme, IButton, IIconProps, IIconStyles, mergeStyles, Spinner, SpinnerSize, Stack, StackItem, Text } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { getFileItemIdFromRelativePath, getFilePreview, getItemWebViewUrl } from "../../src/taskpane/services/GraphService";
import authModule from "../../utilities/AuthModule";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { fetchWithRetry } from "../../utilities/FetchRetryUtil";
import { ZoomableImage } from "../../src/taskpane/components/_v2/components/ImageRenderer/ZoomableImage";
const notFoundIconSrc = require("../../assets/not_found.svg");

const theme = getTheme();
const textNotAvailable = mergeStyles({
    color: theme.palette.neutralPrimary,
    fontSize: FontSizes.size18,
    fontWeight: FontWeights.light
});

const contentStyles = mergeStyles({
    height: 'calc(100vh - 80px)',
    marginTop: 10,
    width: "100%",
    overflow: "hidden",
    "iframe": {
        height: "100%",
        width: "100%",
        border: "none",
        overflow: "hidden",
    }
});

const notFoundContentStyles = mergeStyles({
    height: 'calc(100vh - 80px)',
    marginTop: 10,
    width: "100%"
});

// const imageContainerStyles: React.CSSProperties = { height: "100%", display: "flex", alignItems: "center", justifyContent: "center" };

const navigationButtonIcon: IIconStyles = { root: { fontSize: 32 } };
const backButtonIcon: IIconProps = { iconName: "NavigateBack", styles: navigationButtonIcon };
const forwardButtonIcon: IIconProps = { iconName: "NavigateBackMirrored", styles: navigationButtonIcon };

export enum SearchSource {
    Sharepoint,
    Graph,
    Api
}

export enum MoveDirection {
    Back,
    Forward
}

const checkIsImage = (fileName: string): boolean => {
    if (!fileName) {
        return false;
    }

    const imageExtensions = [
        'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tif', 'tiff'
    ];

    const fileExtension = fileName.split('.').pop()?.toLowerCase();

    if (!fileExtension) {
        return false;
    }

    return imageExtensions.includes(fileExtension);
}

export interface ICMFilePreviewPanelProps {
    selectedItem: any;
    searchSource: SearchSource;
    isInOneDrive?: boolean;
    totalItemCount?: number;
    selectedItemIndex?: number;
    previewNotSupported?: boolean;
    isPropertiesPreview?: boolean;
    isMoveBackDisabled?: boolean;
    isMoveForwardDisabled?: boolean;
    move?: (direction: MoveDirection) => void;
}

export const CMFilePreviewPanel = ({ selectedItem, searchSource, isInOneDrive, totalItemCount, selectedItemIndex, previewNotSupported, isPropertiesPreview, isMoveBackDisabled, isMoveForwardDisabled, move }: ICMFilePreviewPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories?.workingDirectories[state.workingDirectories?.workingDirectories?.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);

    const isBackButtonDisabled = React.useMemo(() => selectedItemIndex <= 0 || isMoveBackDisabled, [selectedItemIndex, isMoveBackDisabled]);
    const isForwardButtonDisabled = React.useMemo(() => (selectedItemIndex + 1) >= totalItemCount || isMoveForwardDisabled, [selectedItemIndex, totalItemCount, isMoveForwardDisabled]);

    const [previewURL, setPreviewURL] = React.useState<string | undefined>();
    const [isImage, setIsImage] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const [hasPreviewLoaded, _setHasPreviewLoaded] = React.useState<boolean>(false);
    const [imageStyles, setImageStyles] = React.useState<React.CSSProperties | undefined>();

    const objectUrlRef = React.useRef<string | undefined>(undefined);

    const hasPreviewLoadedRef = React.useRef<boolean>(false);
    const setHasPreviewLoaded = (hasLoaded: boolean) => {
        hasPreviewLoadedRef.current = hasLoaded;
        _setHasPreviewLoaded(hasLoaded);
    };

    const backNavButtonRef = React.useRef<IButton>();
    const forwardNavButtonRef = React.useRef<IButton>();

    const onKeyDown = React.useCallback((event: KeyboardEvent) => {
        if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) {
            switch (event.key) {
                case "ArrowLeft":
                    if (!isBackButtonDisabled) {
                        move(MoveDirection.Back);
                    }
                    break;
                case "ArrowRight":
                    if (!isForwardButtonDisabled) {
                        move(MoveDirection.Forward);
                    }
                    break;
                default:
                    return;
            }
            event.preventDefault();
            event.stopPropagation();
        }
    }, [isBackButtonDisabled, isForwardButtonDisabled, move]);

    React.useEffect(() => {
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [onKeyDown]);

    React.useEffect(() => {
        setHasPreviewLoaded(false);

        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = undefined;
        }

        if (previewNotSupported === true) {
            setPreviewURL(undefined);
            setIsLoading(false);
            setHasPreviewLoaded(true);
        }
        else {
            (async () => {
                setIsLoading(true);
                try {
                    if (selectedItem) {
                        let itemId = "";
                        let driveId = "";
                        let isImageItem: boolean;

                        if (searchSource === SearchSource.Sharepoint) {
                            driveId = isInOneDrive ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);

                            const spItemUrlparts = selectedItem['.spItemUrl'].split('/');
                            itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];

                            if (!itemId) {
                                const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
                                const folderUrl = new URL(driveUrl.origin + selectedItem.FileRef);
                                itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
                            }

                            isImageItem = checkIsImage(selectedItem[".fileType"]);
                        }
                        else if (searchSource === SearchSource.Graph) {
                            itemId = selectedItem.resource.id;
                            driveId = selectedItem.resource.parentReference?.driveId;
                            isImageItem = checkIsImage(selectedItem.resource?.name);
                        }
                        else if (searchSource === SearchSource.Api) {
                            driveId = selectedItem.driveid;
                            itemId = selectedItem.itemid ?? await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, selectedItem.serverrelativepath);
                            isImageItem = checkIsImage(selectedItem.name);
                        }

                        setIsImage(isImageItem);

                        if (itemId && driveId) {
                            let itemPreviewUrl: string;
                            let imageItemStyles: React.CSSProperties | undefined;

                            if (isImageItem) {
                                try {
                                    const response: Response = await fetchWithRetry(`https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}/thumbnails/0/large/content`, {
                                        headers: {
                                            Authorization: `Bearer ${await authModule.getGraphAccessToken()}`,
                                        },
                                    });

                                    if (response.ok) {
                                        const imgBlob = await response.blob();

                                        const bitmap: ImageBitmap = await createImageBitmap(imgBlob);
                                        const { width, height } = bitmap;

                                        if (height >= 800) {
                                            imageItemStyles = {
                                                height: "100%",
                                                maxHeight: 1400
                                            }
                                        }
                                        else if (width >= 800) {
                                            imageItemStyles = {
                                                width: "100%"
                                            }
                                        }

                                        bitmap.close()

                                        const newObjectUrl = URL.createObjectURL(imgBlob);
                                        objectUrlRef.current = newObjectUrl;
                                        itemPreviewUrl = newObjectUrl;
                                        setHasPreviewLoaded(true);
                                    }
                                } catch (error) {
                                    // Ignore
                                }
                            }

                            if (!itemPreviewUrl) {
                                const preview = await getFilePreview(driveId, itemId);
                                if (preview) {
                                    const previewUrl = new URL(preview);
                                    previewUrl.searchParams.append("nb", "true");
                                    itemPreviewUrl = previewUrl.href;
                                }
                            }

                            setImageStyles(imageItemStyles);
                            setPreviewURL(itemPreviewUrl);
                        } else {
                            setPreviewURL(undefined);
                        }
                    }
                } catch (error) {
                    setHasPreviewLoaded(true);
                    logger(error, "CMFilePreviewPanel.tsx (init)");
                }
                setIsLoading(false);
            })();
        }

        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = undefined;
            }
        };
    }, [selectedItem, previewNotSupported]);

    React.useEffect(() => {
        let intervalId: NodeJS.Timeout;
        if (!isPropertiesPreview && !isLoading && !hasPreviewLoadedRef.current && (!isBackButtonDisabled || !isForwardButtonDisabled)) {
            let counter: number = 0;
            intervalId = setInterval(() => {
                if (!isForwardButtonDisabled) {
                    forwardNavButtonRef.current?.focus();
                }
                else {
                    backNavButtonRef.current?.focus();
                }

                if (++counter >= 20) {
                    clearInterval(intervalId);
                }
            }, 250);
        }
        return () => {
            clearInterval(intervalId);
        };
    }, [isPropertiesPreview, isLoading, hasPreviewLoaded, isBackButtonDisabled, isForwardButtonDisabled]);

    const handleIframeLoad = () => {
        setTimeout(() => setHasPreviewLoaded(true), 2000);
    }

    return (
        <>
            <Stack verticalAlign="space-evenly" style={{ height: "100%" }}>
                <Stack horizontal horizontalAlign="center" verticalAlign="center">
                    <ActionButton className="previewNavButton" componentRef={backNavButtonRef} iconProps={backButtonIcon} disabled={isBackButtonDisabled} onClick={() => move(MoveDirection.Back)} />
                    <div className={contentStyles}>
                        {!isLoading && previewURL && (
                            !isImage
                                ? <iframe src={previewURL} onLoad={handleIframeLoad} />
                                // : <div style={imageContainerStyles}><img src={previewURL} style={imageStyles} /></div>
                                : <ZoomableImage src={previewURL} imageStyles={imageStyles} />
                        )}
                        {!isLoading && !previewURL &&
                            <Stack disableShrink className={notFoundContentStyles} verticalAlign="center" horizontalAlign="center" grow>
                                <StackItem>
                                    <img src={notFoundIconSrc} style={{ paddingBottom: 15 }} />
                                    <Text className={textNotAvailable}>{languageStrings.PreviewNotAvailable}</Text>
                                </StackItem>
                            </Stack>
                        }
                    </div>
                    <ActionButton autoFocus className="previewNavButton" componentRef={forwardNavButtonRef} iconProps={forwardButtonIcon} disabled={isForwardButtonDisabled} onClick={() => move(MoveDirection.Forward)} />
                </Stack>
            </Stack>
            {isLoading && !isPropertiesPreview &&
                <div className="xb-loader-container">
                    <Spinner size={SpinnerSize.medium} label={languageStrings.Loading} />
                </div>
            }
        </>
    )
};